import React, { useEffect, useState } from 'react'
import { useUser } from './context/user'
import './styles/App.css'
import './styles/AuthenticatedApp.css'
import { useToasts } from 'react-toast-notifications'
import { getByToken } from './server/server'
import usePageVisibility from './utils/usePageVisibility'
import { useAuth } from './context/auth'
import LandingPage from './components/LandingPage'
import { useSocket } from './context/socket'
import * as Sentry from '@sentry/react'
import { logLoginTime } from './utils/usersData'
import { useTranslation } from 'react-i18next'
import { Route, Redirect, Switch, useHistory } from 'react-router-dom'
import Menu from './components/Menu'
import HomeScreen from './components/homescreen/HomeScreen'
import PrivateRoute from './router/PrivateRoute'
import PublicRoute from './router/PublicRoute'
import Login from './components/auth/Login'
import NewPassword from './components/auth/NewPassword'
import Token from './components/auth/Token'
import EnterNewPassword from './components/auth/EnterNewPassword'
import TermsAndConditionsModal from './components/Modal/TermsAndConditionsModal'
import InstructionVideoModal from './components/Modal/InstructionVideoModal'
import { getUnseenConversations, ifUserNeedsTutorial, logUserTimeAsync } from './server/server'
import Solutions from './components/solutions/Solutions'
import Chat from './components/Chat'
import Achievements from './components/achievements/Achievements'
import Profile from './components/Profile'
import PrivateNotes from './components/PrivateNotes'
import PersonalInfo from './components/PersonalInfo'
import PrivacyAndSecurity from './components/PrivacyAndSecurity'
import AdvancedSettings from './components/AdvancedSettings'
import SolutionsGroup from './components/solutions/SolutionsGroup'
import ImmediateIssueSolutions from './components/solutions/ImmediateIssueSolutions'
import OtherStudentsIssueSolutions from './components/solutions/OtherStudentsIssueSolutions'
import ContactCoachSolution from './components/solutions/ContactCoachSolution'
import ChatScreen from './components/chat/ChatScreen'
const SECTIONS = ['feed', 'home', 'achievements', 'chat', 'tasks', 'profile', 'solutions']

function App() {
    const { user, setUser, rewardingSystemEnabled, isSignedIn } = useUser()
    const { addToast } = useToasts()
    const { logoutUser } = useAuth()
    const [authLoading, setAuthLoading] = useState(true)
    const { t } = useTranslation()
    const searchParams = new URLSearchParams(window.location.search)
    const authToken = searchParams.get('token') ?? null
    const [activeSection, setActiveSection] = useState('home')
    const [instructionVideoVisible, setInstructionVideoVisible] = useState(false)

    const { i18n } = useTranslation()
    const [showTutorial, setTutorial] = useState(false)
    const [isTCModalVisible, setTCModalVisible] = useState(false)
    const [activeTime, setActiveTime] = useState(Date.now())
    const history = useHistory()
    const { socketStates, changeSocketState } = useSocket()
    const pageIsNotVisible = usePageVisibility()

    const checkLogin = () => {
        const loginDate = parseInt(localStorage.getItem('loginDate'))
        const temporaryAccess = localStorage.getItem('temporaryAccess') === 'true'
        const minute = 60000
        if (((!!loginDate && Date.now() - loginDate > minute * 15) || !loginDate) && temporaryAccess) {
            localStorage.clear()
            setUser(null)
            if (!!loginDate)
                addToast(t('You have been logged out automatically after 15 minutes'), {
                    appearance: 'info',
                    autoDismiss: true,
                })
        }
    }

    const uploadLoginLogs = async () => {
        let restLogins = JSON.parse(localStorage.getItem('loginsLogs') || '[]')
        while (restLogins.length > 0) {
            const data = restLogins.pop()
            const status = await logLoginTime(data, false)
            if (!status) {
                return
            }
        }
        localStorage.setItem('loginsLogs', '[]')
    }

    const changeVisibility = () => {
        checkLogin()
        uploadLoginLogs()
    }

    useEffect(() => {
        changeVisibility()
        window.addEventListener('visibilitychange', changeVisibility)
        return () => {
            window.removeEventListener('visibilitychange', changeVisibility)
        }
    }, [])

    const checkUserAuth = async () => {
        if (authToken) {
            localStorage.setItem('userToken', authToken)
            searchParams.delete('token')
            const newUrl = `${window.location.pathname}`
            window.history.replaceState(null, '', newUrl)
        }

        const resp = await getByToken()
        if (resp?.status === 200) {
            const data = resp.data
            const user = data
            localStorage.setItem('userToken', data['token'])
            localStorage.setItem('user', JSON.stringify(data))
            setUser(user)
            setAuthLoading(false)
        } else if (resp?.status === 406) {
            logoutUser()
            setAuthLoading(false)
        } else {
            logoutUser()
            setAuthLoading(false)
        }
    }

    useEffect(() => {
        checkUserAuth()
    }, [])

    //new
    const toggleInstructionVideo = () => {
        setInstructionVideoVisible(!instructionVideoVisible)
    }

    useEffect(() => {
        let currentSection = SECTIONS.find((s) => window.location.href.includes(s))
        if (currentSection) setActiveSection(currentSection)
    }, [])

    useEffect(() => {
        if (!!user) loadUserData()
    }, [user])

    useEffect(() => {
        const sum = socketStates?.unseenConversations?.length
        document.title = `AlkalmaUp ${sum ? `(${sum})` : ''}`
    }, [socketStates])

    useEffect(() => {
        if (!!user?.first_name) {
            if (!pageIsNotVisible) setActiveTime(Date.now())
            else logUserTime()
        }
    }, [pageIsNotVisible, user])

    useEffect(() => {
        window.addEventListener('beforeunload', logUserTime)
        return () => {
            window.removeEventListener('beforeunload', logUserTime)
        }
    }, [])

    const logUserTime = () => {
        const data = {
            start_time: activeTime,
            end_time: Date.now(),
            device: 'desktop',
        }
        logUserTimeAsync(data)
        setActiveTime(Date.now())
    }

    const loadUserData = async () => {
        const unseenConversations = await loadUnseenConversations()
        const showTutorial = await checkIfUserNeedsTutorial()
        if (showTutorial) setTutorial(true)
        console.log('User:', user.agree, !user?.agree && ['en', 'de'].includes(i18n.language))

        if (!user?.agree && ['en', 'de'].includes(i18n.language)) setTCModalVisible(true)
        changeSocketState({ tasks: [], unseenConversations: unseenConversations })
    }

    const loadUnseenConversations = async () => {
        const resp = await getUnseenConversations()
        if (resp.status === 200) {
            return resp?.data || []
        }
        return []
    }

    const checkIfUserNeedsTutorial = async () => {
        const resp = await ifUserNeedsTutorial()
        if (resp.status === 200) {
            return resp?.data ?? false
        }
        return false
    }

    const onDisagree = async () => {
        setTCModalVisible(false)
        history?.push('/')
        logoutUser()
    }

    return !authLoading ? (
        <>
            <div id="home" className="App">
                {isSignedIn && <Menu activeSection={activeSection} setActiveSection={setActiveSection} toggleInstructionVideo={toggleInstructionVideo} />}
                <div className={isSignedIn ? 'authenticated-section' : ''}>
                    <Switch>
                        <Route exact path="/">
                            {isSignedIn ? <Redirect to="/home" /> : <Redirect to="/login" />}
                        </Route>
                        <Route path="/login" render={() => <PublicRoute Component={Login} />} />
                        <Route path="/new-password" render={() => <PublicRoute Component={NewPassword} />} />
                        <Route path="/token" render={() => <PublicRoute Component={Token} />} />
                        <Route path="/enter-new-password" render={() => <PublicRoute Component={EnterNewPassword} />} />

                        <Route path="/home" render={() => <PrivateRoute Component={HomeScreen} />} />
                        <Route path="/feed" render={() => <PrivateRoute Component={HomeScreen} />} />
                        <Route path="/solutions" exact render={() => <PrivateRoute Component={Solutions} />} />

                        <Route exact path="/solutions/:id/immediate-solutions" render={() => <PrivateRoute Component={ImmediateIssueSolutions} />} />
                        <Route exact path="/solutions/:id/other-student-solutions" render={() => <PrivateRoute Component={OtherStudentsIssueSolutions} />} />
                        <Route exact path="/solutions/:id/contact-coach-solution" render={() => <PrivateRoute Component={ContactCoachSolution} />} />
                        <Route exact path="/solutions/:id" render={() => <PrivateRoute Component={SolutionsGroup} />} />
                        <Route path="/chat" exact render={() => <PrivateRoute Component={ChatScreen} />} />

                        {rewardingSystemEnabled && <Route path="/achievements" exact render={() => <PrivateRoute Component={Achievements} />} />}

                        <Route path="/profile" exact render={() => <PrivateRoute Component={Profile} />} />
                        <Route path="/profile/private-notes" render={() => <PrivateRoute Component={PrivateNotes} />} />
                        <Route path="/profile/personal-info" render={() => <PrivateRoute Component={PersonalInfo} />} />
                        <Route path="/profile/privacy-and-security" render={() => <PrivateRoute Component={PrivacyAndSecurity} />} />
                        <Route path="/profile/advanced-settings" render={() => <PrivateRoute Component={AdvancedSettings} />} />
                        <Route path="*">
                            <Redirect to="/home" />
                        </Route>

                        {!isSignedIn && (
                            <Route path="*">
                                <Redirect to="/login" />
                            </Route>
                        )}
                    </Switch>
                </div>
            </div>
            {isSignedIn && (
                <>
                    <TermsAndConditionsModal closeModal={() => setTCModalVisible(false)} modalIsOpen={isTCModalVisible} onDisagree={onDisagree} />
                    {/* <InstructionVideoModal isOpen={instructionVideoVisible} onRequestClose={toggleInstructionVideo} /> */}
                </>
            )}
        </>
    ) : (
        <LandingPage />
    )
}

export default Sentry.withProfiler(App)
